<script>
  import { onMount } from "svelte";

  import { apiMethods } from "../../Services/index.svelte";

  import {
    Button,
    NotificationModal,
    PageLoading,
    SvelteTable,
  } from "../../Components/Common/index.svelte";

  import { isMobile, user } from "../../stores";

  import {
    getAccountCalendarRepeatIntervalType,
    getBackEndDeleteType,
    getDateFromDateString,
    getFormatDateAndTime,
    getText,
    getTodayWithTime,
    replaceURL,
    showError,
    showException,
  } from "../../Utils/utilFunctions.svelte";

  import {
    DeleteRepeatingEventOptions,
    DirectUserRoles,
  } from "../../Utils/utilEnums.svelte";

  import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

  import SearchInput from "../../Components/Common/SearchInput.svelte";
  import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
  import GlobalEventModal from "../Account/components/GlobalEventModal.svelte";
  import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

  let params = {};

  let isGettingAccountCalendarEvents = false,
    returnedGlobalEvents = [];

  let deleteAccountCalendarEventModalOpen = false,
    isDeletingAccountCalendarEvent = false;

  let filters = { dateFrom: new Date(), sort: "fromdate" };

  let page = 0,
    pageSize = 25,
    count,
    isEventModalOpen = false,
    eventId,
    newValues,
    deleteType,
    showFilters = false,
    eventToDelete;

  $: isDeleteEventButtonDisabled =
    eventToDelete && eventToDelete.repeatIntervalType && !deleteType;

  $: allowGroupEdit = $user.role === DirectUserRoles.Admin;

  const columns = [
    {
      key: "fromDate",
      title: "From Date",
      headerClass:
        "text-left px-3 rounded-tl-xl cursor-pointer sortable-header-decoration",
      class: "text-left pl-3 cursor-default",
      value: (v) => getFormatDateAndTime(v.fromDate),
    },
    {
      key: "toDate",
      title: "To Date",
      headerClass: "text-left px-3 cursor-default",
      class: "text-left pl-3 cursor-default",
      value: (v) => getFormatDateAndTime(v.toDate),
    },
    {
      key: "description",
      title: "Description",
      headerClass: "text-left px-3 cursor-default",
      class: "text-left pl-3 cursor-default",
      value: (v) => getText(v.description),
    },
    {
      key: "recurrence",
      title: "Recurrence",
      headerClass: "text-left px-3 cursor-default",
      class: "text-left pl-3 cursor-default",
      value: (v) => _getRecurrentType(v),
    },
    {
      key: "buttons",
      title: "Actions",
      headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
      class: "px-3 cursor-default",
      renderValue: (v) => _renderActionIcons(),
    },
  ];

  const groupColumn = {
    key: "groupName",
    title: "Group Name",
    headerClass: "text-left px-3 w-1/8 cursor-default",
    class: "text-left px-3 cursor-pointer",
    renderValue: (v) => getText(v.groupName ?? "-"),
  };

  onMount(() => {
    if (params.query) _updateSearchParamsByQuery(params.query);

    if (allowGroupEdit) columns.splice(2, 0, groupColumn);

    loadGlobalEvent(page * pageSize, pageSize, _getSearchParams());
  });

  const loadGlobalEvent = async (start, limit, params) => {
    isGettingAccountCalendarEvents = true;

    if ($user.role === DirectUserRoles.Manager && $user.groupId)
      params["filter[groupId]"] = $user.groupId;

    await apiMethods.getMethod
      .getGlobalEvents(start, limit, params)
      .then((res) => _handleAccountCalendarEvents(res))
      .catch((err) => showException(err));

    isGettingAccountCalendarEvents = false;
  };

  const updateHeaderSortIcon = () => {
    let thRow = document.getElementsByTagName("th");

    if (filters.sort) {
      setTimeout(() => {
        const upSpan = document.createElement("span");
        const downSpan = document.createElement("span");
        upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
        upSpan.style = "font-size: 10px;";
        downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
        downSpan.style = "font-size: 10px;";

        Array.from(thRow).forEach((row) => {
          if (
            filters.sort == "fromdate" &&
            row.childNodes[0].data == "From Date"
          )
            row.appendChild(upSpan);
          else if (
            filters.sort == "fromdatedesc" &&
            row.childNodes[0].data == "From Date"
          )
            row.appendChild(downSpan);
        });
      }, 0);
    }
  };

  const deleteAccountCalendarEvent = async () => {
    isDeletingAccountCalendarEvent = true;

    await apiMethods.deleteMethod
      .deleteGlobalEvent(eventId, getBackEndDeleteType(deleteType))
      .then((res) => {
        if (!res || !res.id)
          showError(
            "Failed to delete calendar event, please refresh the page and try again and contact support if the issue persists.",
          );
        else loadGlobalEvent(page * pageSize, pageSize, _getSearchParams());
      })
      .catch((err) => showException(err));

    _closeDeleteAccountLeaveModal();

    isDeletingAccountCalendarEvent = false;
  };

  const handleFilterSelect = () => {
    page = 0;

    updateURL();

    loadGlobalEvent(0, pageSize, _getSearchParams());
  };

  const updateURL = () => {
    replaceURL(
      `/GlobalEvents?&page=${page}&size=${pageSize}${_getSearchParams()}`,
    );
  };

  const handleClickCell = (event, rowData, key) => {
    const { className } = event.target;

    if (key == "buttons") {
      if (className.includes("deleteIcon"))
        _openDeleteAccountLeaveModal(rowData);
      else if (className.includes("editIcon")) openEditModal(rowData.id);
    }
  };

  const onPageChange = (e) => {
    page = Number(e.detail.page);

    updateURL();

    loadGlobalEvent(
      Number(e.detail.firstDataIndex),
      pageSize,
      _getSearchParams(),
    );
  };

  const onPageSizeChange = (newPageSize) => {
    pageSize = newPageSize;
    page = 0;

    updateURL();

    loadGlobalEvent(0, pageSize, _getSearchParams());
  };

  // HELPER FUNCTIONS
  const _handleAccountCalendarEvents = (res) => {
    if (!res)
      showError(
        "Failed to load leaves data, please refresh the page and try again and contact support if the issue persists.",
      );
    else if (res.error) showError(res.error);
    else {
      returnedGlobalEvents = [...res.results];
      count = res.total;
    }

    updateHeaderSortIcon();
  };

  const _updateSearchParamsByQuery = (query) => {
    const arr = query.split("&");

    if (arr.length) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].includes("page=")) page = Number(arr[i].split("=")[1]);

        if (arr[i].includes("size=")) pageSize = Number(arr[i].split("=")[1]);

        if (arr[i].includes("search="))
          filters.search = arr[i].split("=")[1].toString();
      }
    }
  };

  const _getSearchParams = () => {
    let params = {};

    if (filters.sort) params.sort = filters.sort;

    const dateFrom = getDateFromDateString(filters.dateFrom)
      .split(", ")[0]
      .split("/")
      .reverse()
      .join("-");

    const dateTo = getDateFromDateString(filters.dateTo)
      .split(", ")[0]
      .split("/")
      .reverse()
      .join("-");

    if (dateFrom.includes("NaN") || dateTo.includes("NaN"))
      return location.reload();

    if (dateFrom) params["filter[fromDateFrom]"] = dateFrom;

    if (dateTo) params["filter[toDateTo]"] = dateTo;

    return params;
  };

  const _getRecurrentType = (event) => {
    const { repeatIntervalType } = event;

    if (!repeatIntervalType) return "-";

    return getAccountCalendarRepeatIntervalType(repeatIntervalType);
  };

  const _renderActionIcons = () => {
    return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Event">
                        <i class="editIcon cursor-pointer ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Event">
                        <i class="deleteIcon cursor-pointer ${deleteIcon}"></i>
                    </div>
                </div>`;
  };

  const openEditModal = (id) => {
    if (!id) return;
    eventId = id;
    newValues = {};
    isEventModalOpen = true;
  };

  const openAddEventModal = () => {
    newValues = {};
    eventId = null;
    isEventModalOpen = true;
  };

  const _openDeleteAccountLeaveModal = (row) => {
    eventToDelete = row;
    deleteAccountCalendarEventModalOpen = true;
    eventId = Number(row.id);
  };

  const _closeDeleteAccountLeaveModal = () => {
    deleteAccountCalendarEventModalOpen = false;
    deleteType = undefined;
    eventToDelete = undefined;

    eventId = Number(0);
  };

  const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

  const sortableHeaderKeys = ["fromDate"];

  const handleClickCol = ({ target: { className } }, colData, key) => {
    if (sortableHeaderKeys.includes(key)) {
      if (filters.sort == key.toLowerCase())
        filters.sort = `${key}desc`.toLowerCase();
      else filters.sort = key.toLowerCase();
      updateURL();
      loadGlobalEvent(0, pageSize, _getSearchParams());
    }
  };
</script>

<div class="flex flex-col flex-1 gap-y-4 pb-6">
  {#if $isMobile}
    <button
      class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
      on:click={() => (showFilters = !showFilters)}
    >
      Filters
      <i
        style="font-size:20px"
        class={showFilters ? "fa arrow-up" : "fa arrow-down"}
        >&#xf104;
      </i>
    </button>
  {/if}
  {#if showFilters || !$isMobile}
    <div
      class="flex flex-0 gap-4 {$isMobile
        ? 'items-start flex-col'
        : 'justify-between flex-row'}"
      in:filterTransition={{ duration: 200, y: -100 }}
    >
      <div class="flex flex-row items-center justify-between flex-wrap">
        <div class="flex gap-4 flex-wrap">
          {#if !$user.settings?.disableDateRangeInput}
            <SearchDateRangeInput
              bind:startDate={filters.dateFrom}
              bind:endDate={filters.dateTo}
              title="Date Range"
              inputClass="borderB w-[272px]"
              on:change={handleFilterSelect}
              showClearButton
              disable={isGettingAccountCalendarEvents}
              showScrollYearOptions
            />
          {:else}
            <SearchInput
              bind:value={filters.dateFrom}
              title="Date from"
              type="date"
              isLong
              inputClass="borderB"
              inputFunc={handleFilterSelect}
              isDisabled={isGettingAccountCalendarEvents}
              maxDate={filters.dateTo ?? undefined}
              showScrollYearOptions
            />

            <SearchInput
              bind:value={filters.dateTo}
              title="Date to"
              type="date"
              isLong
              inputClass="borderB"
              inputFunc={handleFilterSelect}
              isDisabled={isGettingAccountCalendarEvents}
              minDate={filters.dateFrom ?? undefined}
              showScrollYearOptions
            />
          {/if}
        </div>
      </div>
      <Button clickFunc={openAddEventModal}>Create Global Event</Button>
    </div>
  {/if}

  <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
    {#if isGettingAccountCalendarEvents}
      <PageLoading />
    {:else if returnedGlobalEvents.length}
      <div
        class={$isMobile
          ? "overflow-x-auto overflow-y-visible"
          : "overflow-y-auto absolute inset-0"}
      >
        <SvelteTable
          {columns}
          rows={returnedGlobalEvents}
          clickCellFunc={({ detail: { event, row, key } }) => {
            handleClickCell(event, row, key);
          }}
          clickColFunc={({ detail: { event, row, key } }) => {
            handleClickCol(event, row, key);
          }}
        />
      </div>
    {/if}
  </div>
</div>

<TableFooter
  {page}
  {pageSize}
  {count}
  tableData={returnedGlobalEvents}
  {onPageChange}
  {onPageSizeChange}
  csvConfig={{
    columns,
    data: returnedGlobalEvents,
    fileName: "Global Events",
    allDataApi: async () =>
      await apiMethods.getMethod.getGlobalEvents(0, 999999, _getSearchParams()),
  }}
/>

<GlobalEventModal
  id={eventId}
  bind:isModalOpen={isEventModalOpen}
  on:eventsChanged={() =>
    loadGlobalEvent(page * pageSize, pageSize, _getSearchParams())}
/>

<NotificationModal
  title="Delete Event"
  submitText="Cancel"
  confirmButtonClass="btn-accent"
  cancelText={isDeletingAccountCalendarEvent ? "Deleting" : "Confirm"}
  isModalOpen={deleteAccountCalendarEventModalOpen}
  isConfirming={isDeletingAccountCalendarEvent}
  confirmAction={_closeDeleteAccountLeaveModal}
  closeModal={deleteAccountCalendarEvent}
  isReverseBtns={true}
  closeModalFunction={_closeDeleteAccountLeaveModal}
  isCancelDisabled={isDeleteEventButtonDisabled}
>
  {#if eventToDelete.repeatIntervalType > 0}
    <p class="whitespace-pre-line mb-4">Do you want to delete:</p>
    <div class="flex flex-col gap-y-4 font-medium">
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.thisOnly}
          bind:group={deleteType}
        />
        This event only
      </label>
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.thisAndFollowing}
          bind:group={deleteType}
        />
        This and following events
      </label>
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.allEvents}
          bind:group={deleteType}
        />
        All events
      </label>
    </div>
  {:else}
    <p class="whitespace-pre-line">Do you want to delete this global event?</p>
  {/if}
</NotificationModal>
